import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { showAppAlert } from '../utils/alertService';
import { findInvalidEmailRecipients, parseKnownProviderEmails } from '../utils/emailValidation';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function EditAlertModal({ isOpen, onClose, selectedAlert, onUpdate }) {
  const [formData, setFormData] = useState({
    nome: '',
    mensagem: '',
    destinatarios: [],
    data_inicio: '',
    data_termino: '',
    status: 0,
  });

  const [manualRecipient, setManualRecipient] = useState('');
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [recipientSelection, setRecipientSelection] = useState('');

  useEffect(() => {
    if (selectedAlert) {
      const recipientsArray = selectedAlert.destinatarios ? selectedAlert.destinatarios.split(',').map(r => r.trim()).filter(r => r) : [];
      setFormData({
        nome: selectedAlert.nome || '',
        mensagem: selectedAlert.mensagem || '',
        destinatarios: recipientsArray,
        data_inicio: selectedAlert.data_inicio || '',
        data_termino: selectedAlert.data_termino || '',
        status: selectedAlert.status === 'Ativo' ? 1 : 0,
      });

      if (recipientsArray.includes('all')) {
        setRecipientSelection('all_clients');
      } else {
        setRecipientSelection('');
      }

      // Reseta os campos de controle de destinatários
      setManualRecipient('');
      setIsManualEntry(false);
    }
  }, [selectedAlert]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const today = new Date().toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .split('/')
      .reverse()
      .join('-');
    if (formData.data_inicio < today) {
      showAppAlert('warning', 'A data de início não pode ser inferior à data de hoje.');
      return;
    }
    if (formData.data_termino < formData.data_inicio) {
      showAppAlert('warning', 'A data de término não pode ser inferior à data de início.');
      return;
    }

    if (formData.destinatarios.length === 0) {
      showAppAlert('warning', 'O destinatário não pode estar vazio.');
      return;
    }

    const invalidRecipients = findInvalidEmailRecipients(formData.destinatarios);
    if (invalidRecipients.length > 0) {
      showAppAlert('warning', `Remova os e-mails inválidos ou de provedores não permitidos: ${invalidRecipients.join(', ')}`);
      return;
    }

    const token = localStorage.getItem('token');

    const alertData = {
      alert_name: formData.nome,
      alert_msg: formData.mensagem,
      recipients: JSON.stringify(formData.destinatarios),
      start_date: formData.data_inicio,
      end_date: formData.data_termino,
      status: Number(formData.status),
    };

    // Adiciona a hora para manter o formato esperado pela API
    alertData.start_date = `${alertData.start_date} 08:00:00`;
    alertData.end_date = `${alertData.end_date} 23:59:59`;

    try {
      const response = await fetch(`${API_URL}/alerts/${selectedAlert.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(alertData),
      });

      if (response.ok) {
        const updatedAlert = await response.json();
        showAppAlert('success', 'Alerta atualizado com sucesso!');
        onUpdate(updatedAlert);
        onClose();
      } else {
        const errorData = await response.json();
        showAppAlert('error', errorData.message || 'Erro ao atualizar o alerta.');
      }
    } catch (error) {
      console.error('Erro ao atualizar o alerta:', error);
      showAppAlert('error', `Erro de conexão ao atualizar o alerta: ${error.message}`);
    }
  };

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;

    if (!selectedValue) {
      setRecipientSelection('');
      return;
    }

    if (selectedValue === 'manual') {
      setIsManualEntry(true);
      if (formData.destinatarios.includes('all')) {
        setFormData(prev => ({ ...prev, destinatarios: [] }));
      }
      setRecipientSelection('manual');
    } else if (selectedValue === 'all_clients') {
      setIsManualEntry(false);
      setFormData(prev => ({ ...prev, destinatarios: ['all'] }));
      setRecipientSelection('all_clients');
    } else {
      setIsManualEntry(false);
      if (formData.destinatarios.includes(selectedValue)) {
        showAppAlert('info', 'Este segmento já foi adicionado.');
        setRecipientSelection(''); // Reset select
        return;
      }

      const newDestinatarios = formData.destinatarios.filter(d => d !== 'all');
      setFormData(prev => ({
        ...prev,
        destinatarios: [...newDestinatarios, selectedValue],
      }));
      setRecipientSelection(''); // Reset select to allow adding more
    }
  };

  const handleManualRecipientBlur = () => {
    if (!manualRecipient.trim()) return;

    const { valid, invalid } = parseKnownProviderEmails(
      manualRecipient,
      formData.destinatarios
    );

    if (invalid.length > 0) {
      showAppAlert('warning', `Os seguintes e-mails são inválidos ou não pertencem a provedores permitidos: ${invalid.join(', ')}`);
    }

    if (valid.length > 0) {
      setFormData(prev => ({
        ...prev,
        destinatarios: [...prev.destinatarios, ...valid],
      }));
    }

    setManualRecipient('');
  };

  const removeRecipient = (recipientToRemove) => {
    const newDestinatarios = formData.destinatarios.filter(r => r !== recipientToRemove);
    setFormData({
      ...formData,
      destinatarios: newDestinatarios,
    });
    if (recipientToRemove === 'all') {
      setRecipientSelection('');
    }
  };

  const formatRecipient = (recipient) => {
    if (recipient === 'all') return 'Todos os Clientes Ativos';
    if (recipient.startsWith('gender:')) {
      const gender = recipient.split(':')[1];
      return `Gênero: ${gender.charAt(0).toUpperCase() + gender.slice(1)}`;
    }
    if (recipient.startsWith('age:')) {
      const range = recipient.split(':')[1];
      if (range === '0-18') return 'Idade: Até 18 anos';
      if (range === '60-999') return 'Idade: Acima de 60 anos';
      return `Idade: ${range.replace('-', ' a ')} anos`;
    }
    if (recipient.startsWith('location:')) {
      const loc = recipient.split(':')[1];
      return `Localização: ${loc}`;
    }
    return recipient;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Editar Alerta</h3>
        <form onSubmit={handleSubmit}>
          <label className="label-style">
            Nome:
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              className="input-style"
              required
            />
          </label>
          <label className="label-style">
            Mensagem:
            <textarea
              name="mensagem"
              value={formData.mensagem}
              onChange={handleChange}
              className="input-style"
              rows="3"
              required
            />
          </label>
          <label className="label-style">
            Destinatários:
            <select
              name="destinatarios"
              onChange={handleSelectChange}
              className="input-style"
              value={recipientSelection}
            >
              <option value="">Selecione para adicionar...</option>
              <option value="all_clients">Todos os Clientes Ativos</option>
              <optgroup label="Gênero">
                <option value="gender:masculino">Masculino</option>
                <option value="gender:feminino">Feminino</option>
              </optgroup>
              <optgroup label="Faixa Etária">
                <option value="age:0-18">Até 18 anos</option>
                <option value="age:19-35">19 a 35 anos</option>
                <option value="age:36-59">36 a 59 anos</option>
                <option value="age:60-999">Acima de 60 anos</option>
              </optgroup>
              <option value="manual">Inserir Manualmente</option>
            </select>
          </label>

          {isManualEntry && (
            <label className="label-style">
              Inserir Destinatários Manualmente (separados por vírgula):
              <input
                type="text"
                value={manualRecipient}
                onChange={(e) => setManualRecipient(e.target.value)}
                onBlur={handleManualRecipientBlur}
                className="input-style"
                placeholder="usuario@gmail.com, usuario@icloud.com"
              />
            </label>
          )}

          {formData.destinatarios.length > 0 && (
            <div className="recipients-display">
              {formData.destinatarios.slice(0, 10).map((recipient, index) => (
                <span key={index} className="recipient-tag">
                  {formatRecipient(recipient)}
                  <button type="button" onClick={() => removeRecipient(recipient)}>
                    &times;
                  </button>
                </span>
              ))}
              {formData.destinatarios.length > 10 && (
                <span className="recipient-tag-more">
                  ... e mais {formData.destinatarios.length - 10} destinatário(s)
                </span>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="label-style flex-1">
              Data Início:
              <DatePicker
                selected={formData.data_inicio ? new Date(formData.data_inicio + 'T00:00:00') : null}
                onChange={(date) => setFormData({ ...formData, data_inicio: date.toISOString().split('T')[0] })}
                className="input-style"
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/aaaa"
                showYearDropdown
                scrollableYearDropdown
                minDate={new Date()}
                required
              />
            </div>
            <div className="label-style flex-1">
              Data Término:
              <DatePicker
                selected={formData.data_termino ? new Date(formData.data_termino + 'T00:00:00') : null}
                onChange={(date) => setFormData({ ...formData, data_termino: date.toISOString().split('T')[0] })}
                className="input-style"
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/aaaa"
                showYearDropdown
                scrollableYearDropdown
                minDate={formData.data_inicio ? new Date(formData.data_inicio + 'T00:00:00') : new Date()}
                required
              />
            </div>
          </div>
          <label className="label-style">
            Status:
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input-style"
            >
              <option value={1}>Ativo</option>
              <option value={0}>Inativo</option>
            </select>
          </label>
          <div className="button-container">
            <button type="submit" className="button-style">
              Salvar
            </button>
            <button type="button" className="button-style cancel" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditAlertModal;